/**
 * Email-draft approval hooks (Automations Phase 4 — email responder go-live).
 *
 * These surface drafted email REPLIES in the Activity Center's Review tab. As of the
 * cross-object plan (design 2026-06-30) the generic queue + decide logic lives in the shared
 * `common/hooks/useActionApprovals` (so both this feature and the Automations run timeline can
 * reuse it without crossing the `features/`-cannot-import-`modules/` layering line). This module
 * is now a thin EMAIL-NARROWED view over that generic hook: it resolves the session
 * (`token` / `workspaceId`, which `common/` may not reach up for) and keeps only the email-send
 * drafts, mapped to the `EmailDraftApproval` shape the specialized `EmailDraftReviewCard` renders.
 *
 * `useDecideEmailApproval` only records the decision server-side — the actual send is gated
 * separately (mirrors the agent-trust contract).
 */
import { useSession } from '@/features/auth/hooks/useSession';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import {
  useActionApprovals,
  useDecideActionApproval,
  type ActionApproval,
  type DecideActionApprovalVars,
} from '@/common/hooks/useActionApprovals';

/** A pending email-draft approval, flattened for the review card. */
export interface EmailDraftApproval {
  id: string;
  to: string;
  subject: string;
  body: string;
  /** Private note from Xavior about the draft (assumption/caveat). Shown only to the user in the
   *  Review card — it is a sibling of `preparedPayload`, never inside it, so it is never sent. */
  notes?: string;
  /** One-line summary of what the incoming email said — shown above the draft so the user sees what
   *  they're replying to. Like `notes`, a sibling of `preparedPayload`, never sent. */
  incomingSummary?: string;
  /** The original payload, preserved so an edit can patch just `body`. */
  preparedPayload: Record<string, unknown>;
  provenance: Record<string, unknown>;
  noveltyFlags: string[];
  actionType: string;
}

/**
 * Only the email reply drafts render in the email card — `email.send` plus the x-agent tool
 * spelling `send_email` (the alias the gate emits for the same send). Cross-object actions the
 * responder proposes surface through the generic `ActionApprovalCard` instead. Exported so the
 * Activity Center can route EVERYTHING the email card does NOT claim through the generic card
 * (no pending approval is ever rendered nowhere).
 */
export function isEmailReply(a: ActionApproval): boolean {
  return a.actionType === 'email.send' || a.actionType === 'send_email';
}

function toEmailDraft(a: ActionApproval): EmailDraftApproval {
  return {
    id: a.id,
    to: a.email?.to ?? '',
    subject: a.email?.subject ?? '',
    body: a.email?.body ?? '',
    notes: a.notes,
    incomingSummary: a.incomingSummary,
    preparedPayload: a.preparedPayload,
    provenance: a.provenance,
    noveltyFlags: a.noveltyFlags,
    actionType: a.actionType,
  };
}

/**
 * The pending email-draft approvals for the active workspace. Backed by the shared generic
 * approvals query (polls ~15s), narrowed to email replies.
 */
export function useEmailApprovals() {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const q = useActionApprovals(token, workspaceId);
  const items = q.items.filter(isEmailReply).map(toEmailDraft);
  return { ...q, items, workspaceId };
}

export type DecideEmailApprovalVars = DecideActionApprovalVars;

/**
 * Record a decision on an email draft (send / save-and-send / discard). Delegates to the shared
 * decide mutation, which invalidates the approvals query + the activities feed on success.
 */
export function useDecideEmailApproval() {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  return useDecideActionApproval(token, workspaceId);
}
